import type { BalanceData, AprData, LoopPostTradeMetrics } from './types'

export const positivePart = (n: number) => (n < 0 ? 0 : n)

export function nanTo(possiblyNaN: number, replacement = Infinity) {
  return isNaN(possiblyNaN) ? replacement : possiblyNaN
}

export function getHealthFactor(collateral: number, adjustedDebt: number): number {
  if (adjustedDebt === 0) return Infinity
  const raw = collateral / adjustedDebt
  return Math.round((isNaN(raw) ? 0 : raw) * 1e4) / 1e4
}

/** Collect all unique keys from two maps */
export function keysFromMaps<T>(
  a: Record<string, T> | undefined,
  b: Record<string, T> | undefined
): string[] {
  const set = new Set<string>()
  if (a) for (const k of Object.keys(a)) set.add(k)
  if (b) for (const k of Object.keys(b)) set.add(k)
  return [...set]
}

/** Convert a RewardsList array to a map keyed by asset address for fast lookup */
export function rewardsToMap<T extends { asset: string }>(
  rewards: T[] | undefined
): Record<string, T> {
  const map: Record<string, T> = {}
  if (Array.isArray(rewards)) for (const r of rewards) map[r.asset] = r
  return map
}

/** No-op result when dollar amounts are zero */
export function noOpResult(
  balance: BalanceData,
  apr: AprData
): LoopPostTradeMetrics {
  const hf = getHealthFactor(balance.collateral, balance.adjustedDebt)
  const bc = balance.collateral - balance.adjustedDebt
  return {
    pre: { healthFactor: hf, borrowCapacity: bc },
    post: { healthFactor: hf, borrowCapacity: bc, balanceData: balance, aprData: apr },
  }
}

/** Build the final post-trade metrics from updated balance and apr */
export function buildLoopResult(
  balance: BalanceData,
  newBalance: BalanceData,
  apr: AprData,
  newApr: AprData,
  useAllActive = false
): LoopPostTradeMetrics {
  const collateralPost = useAllActive
    ? newBalance.collateralAllActive
    : newBalance.collateral

  return {
    pre: {
      healthFactor: getHealthFactor(balance.collateral, balance.adjustedDebt),
      borrowCapacity: balance.collateral - balance.adjustedDebt,
    },
    post: {
      healthFactor:
        newBalance.adjustedDebt === 0
          ? Infinity
          : positivePart(getHealthFactor(collateralPost, newBalance.adjustedDebt)),
      borrowCapacity: newBalance.collateral - newBalance.adjustedDebt,
      balanceData: newBalance,
      aprData: newApr,
    },
  }
}
